/**
 * activeSolute.js
 * @fileoverview Pairs a case-scoped server command with the `set-active-solute`
 * that scopes it. Lives here, rather than in cmds/cmds_common.js, so that
 * non-cmds senders can use it too: cmds_common imports Loader, and Loader
 * statically pulls in chemistry/LocalMinimizer in dev/qa builds, so anything
 * LocalMinimizer imports from cmds_common would close a cycle. This module's
 * only runtime dependency is App, which itself imports nothing.
 */
import { App } from '../BMapsApp';

// Module-private on purpose. Repointing and then sending as two separately awaited steps
// is the bug this exists to prevent (BP2-2663): the await in between lets another case
// repoint first, so the command lands against the wrong solute. Callers go through
// doWithActiveSolute, which pairs the repoint with the dispatch in a single turn.
async function setSoluteForComponent(item) {
    if (!item) {
        return { connector: App.ServerConnection };
    }
    const { connector, caseData } = App.getDataParents(item);
    if (caseData?.soluteName) await connector.cmdSetActiveSolute(caseData.soluteName);
    return { connector, caseData };
}

/**
 * Send a case-scoped command in the same synchronous turn as the
 * `set-active-solute` that scopes it, so the two arrive back to back.
 *
 * Why this exists, rather than `await setSoluteForComponent(x)` then sending:
 * awaiting the repoint yields to the event loop, and any other caller that
 * repoints during that gap takes the active solute. The command then runs
 * against the wrong case. With multiple structures loaded this is reachable
 * from ordinary use -- clicking an atom on another structure is enough -- and
 * it is why concurrent per-case work (`Promise.all` over several cases) fails
 * for every case but the last. See BP2-2663 / epic BP2-2638.
 *
 * This is safe because of two properties, both verified:
 *  - `ServerConnection.execCmd` writes to the socket *before* it returns
 *    (`server/ServerConnection.js`), so dispatching without awaiting still puts
 *    the frame on the wire immediately; and
 *  - bfd-server drains its message queue on a single thread, one request at a
 *    time (`bfd-server/main.elp`, `processMsgs` -> `ProcessRequest`), so frames
 *    are processed in arrival order.
 *
 * Since JS is single-threaded, nothing can interleave between the two dispatches,
 * so there is no window for another repoint to land.
 *
 * IMPORTANT: `dispatch` must put the command on the wire before it yields. The simple
 * case is returning the promise from a `connector.cmdXxx(...)` call directly. An `async`
 * wrapper is also fine *provided it does no `await` before the send*, since an async
 * function body runs synchronously up to its first `await` -- which is why
 * `UserActions.AssembleCompound(...)` and `compound.updateMolProps()` qualify: both reach
 * their `connector.cmdXxx(...)` before suspending. What breaks it is awaiting anything
 * first, which reopens the window silently. When in doubt, follow the call through to the
 * connector and check nothing is awaited on the way.
 *
 * Holding the dispatched responder across the repoint's `await` is safe only
 * because ServerResponder latches its outcome and replays it to a subscriber that
 * arrives later. Before that fix the response could land in this exact window and
 * be discarded, hanging the caller forever -- so if that latching is ever removed,
 * this function has to subscribe in the same turn as the dispatch instead.
 *
 * @param {*} item item accepted by App.getDataParents (CaseData, Compound, ...)
 * @param {() => Promise<any>} dispatch sends the command and returns its promise
 * @returns {Promise<any>} the command's response
 */
export async function doWithActiveSolute(item, dispatch) {
    const repointPromise = setSoluteForComponent(item); // dispatched, awaited below
    let responsePromise;
    try {
        responsePromise = dispatch(); // same turn as the repoint: no gap
    } catch (ex) {
        // dispatch threw before sending, so the await below is never reached and
        // repointPromise would be abandoned. It is setSoluteForComponent's own async
        // wrapper rather than a ServerResponder, so the responder's no-op catch does not
        // cover it and the rejection escapes as an unhandled one, on top of the error the
        // caller is already getting. Consume it here and let the dispatch error win --
        // it is the actionable one, and it is the reason the repoint was abandoned.
        //
        // Deliberately not an unconditional `repointPromise.catch()` above: that would
        // swallow every repoint failure, not just the ones nothing is left to observe.
        await repointPromise.catch(() => {});
        throw ex;
    }
    await repointPromise;
    return responsePromise;
}
